import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardMedia, Grid, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha } from "@mui/material";
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
import Favorite from '@mui/icons-material/FavoriteBorderOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useMap } from "react-mapkit";
import { AppColor, Fonts } from "../../../../types/Constants";
import { getCategoryColor, getImageURLCategoryListIcon, getImageURLOnServer, getImageURLOnServerBeaconFace } from '../../../../utils/utils';
import { useAuthContext } from "../../../authentication/contexts/AuthContext";
import { Coordinate, GeocoderResponse } from "../../../../types/models/Mapkit";
import { useAppSelector } from "../../../../utils/hooks";
import BeaconCategory from "../../../../components/BeaconCategory";

interface BeaconPreviewProps {
  imageModal?: File,
  imageFace?: File,
  imageId?: string,
  category?: string,
  name?: string,
  beaconId?: string,
  description?: string,
  coordinate?: Coordinate,
  startDate?: string | null,
  hasCustomBeaconFace?: boolean
  lastModified?: string
}

const BeaconPreview: React.FC<BeaconPreviewProps> = (prop) => {

  let auth = useAuthContext();
  const categoryGroupState = useAppSelector((state) => state.category);

  const { mapkit } = useMap();

  const [viewMode, setViewMode] = useState<string>("map");
  const [imageModalURL, setImageModalURL] = useState<string | undefined>();
  const [imageFaceURL, setImageFaceURL] = useState<string | undefined>();
  const [address, setAddress] = useState<string>();

  useEffect(() => {
    if (prop.coordinate && prop.coordinate.latitude !== 0 && prop.coordinate.longitude !== 0) {
      reverseLookupAndSetAddress(prop.coordinate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.coordinate, mapkit]);

  useEffect(() => {
    if (prop.imageModal) {
      // Get file from file instance
      const objectUrl = URL.createObjectURL(prop.imageModal);
      setImageModalURL(objectUrl);
      // free memory when ever this component is unmounted
      return () => URL.revokeObjectURL(objectUrl);
    }
    else if (prop.imageId) {
      // Get imageModal from server
      setImageModalURL(getImageURLOnServer(prop.imageId));
    }
    else {
      setImageModalURL(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.imageModal, prop.imageId]);

  useEffect(() => {
    if (prop.imageFace) {
      // Get file from file instance
      const objectUrl = URL.createObjectURL(prop.imageFace);
      setImageFaceURL(objectUrl);
      // free memory when ever this component is unmounted
      return () => URL.revokeObjectURL(objectUrl);
    }
    else if (!prop.imageFace) {
      if(prop.hasCustomBeaconFace) {
        setImageFaceURL(getImageURLOnServerBeaconFace(prop.beaconId!, prop.lastModified!));
      }
    }
    else {
      setImageFaceURL(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.imageFace, prop.lastModified]);

  const reverseLookupAndSetAddress = (coordinate: Coordinate) => {
    if (mapkit) {
      var geocoder = new mapkit.Geocoder();
      geocoder.getsUserLocation = true; // Lookup from current location
      geocoder.reverseLookup(new mapkit.Coordinate(coordinate.latitude, coordinate.longitude), (error: Error | null, data: GeocoderResponse) => {
        if (error) {
          console.log("Failed to reverseLookup");
          return;
        }

        if (data && data.results && data.results.length > 0) {
          const topResult = data.results[0];
          const addressList = topResult.formattedAddress.split(",");
          let reversedAddress = topResult.formattedAddress;
          if (addressList.length > 2) {
            // Remove country name to be shorter address
            reversedAddress = addressList[0] + ", " + addressList[1];
          }
          setAddress(reversedAddress);
        }
      });
    }
  };

  const getCategory = (categoryName: string) => {
    if (categoryGroupState.categories) {
      const selectedCategory = categoryGroupState.categories.find(category => category.Name === categoryName);
      if (selectedCategory) {
        return selectedCategory;
      }
    }
  };

  const mobilePreview = () => {
    const category = getCategory(prop.category ?? "");
    return (
      <Box className="mobilePreviewContainer" sx={{ display: "flex", justifyContent: "center"}}>
        <Box sx={{ position: "relative", marginTop: "3vh" }}>
          <Box sx={{
            height: "24vh",
            maxHeight: "240px",
            aspectRatio: 3 / 2,
            backgroundColor: "gray",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}>
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", position: "absolute" }}>
              <Box sx={{ width: "20%", height
              : "4px", margin: 1, backgroundColor: "white", borderRadius: 2 }} />
            </Box>
            <Box sx={{ width: "100%", px: 2, display: "flex", justifyContent: "space-between", position: "absolute", top: 10 }}>
              <IconButton size="small" sx={{
                backgroundColor: AppColor.componentBackground,
                border: "1px solid silver",
                height: 32,
                width: 32,
                '&:hover': {
                  backgroundColor: "white",
                }
              }}>
                <ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            {imageModalURL && (
              <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CardMedia
                  sx={{ height: "100%"}}
                  component="img"
                  src={imageModalURL}
                  alt="beacon"
                />
              </Box>)}
          </Box>
          <Box sx={{
            width: "100%",
            position: "absolute",
            aspectRatio: 7 / 8,
            backgroundColor: "white",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            border: "1px solid silver",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}>
            <Box>
              <Box sx={{
                marginTop: -3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingX: 2
              }}>
                <BeaconCategory 
                  category={category?.Name}
                  justifyContent={"left"}
                  icons={true}
                  id={category?.Id}
                />
              </Box>
              <Grid container sx={{ px: 3, marginTop: 2, }}>
                <Grid item xs={7} sx={{ marginTop: 2, }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    <Tooltip title={address}>
                      <Typography
                        style={{ whiteSpace: "pre-line" }}
                        sx={{
                          fontSize: 11,
                          wordBreak: "break-all",
                          color: AppColor.headerText,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical'
                        }} >{address ?? "Location"}</Typography>
                    </Tooltip>
                    <Tooltip title="Get Directions">
                      <IconButton size="small" sx={{
                        '&:hover': {
                          backgroundColor: AppColor.componentBackground,
                        }
                      }}>
                        <NearMeOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={5} sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "100%" }}>
                    <AccountCircleTwoToneIcon sx={{ width: "20px", height: "20px" }} />
                    <Typography
                      style={{ whiteSpace: "pre-line" }}
                      sx={{
                        fontSize: 11,
                        ml: 1,
                        wordBreak: auth.preferredUserName ? "break-word" : "break-all",
                        color: AppColor.headerText,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical'
                      }} >{auth.preferredUserName ?? auth.user}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ marginTop: 2, marginLeft: 3, alignItems: "start" }}>
                <Typography component="h4"
                  sx={{
                    fontWeight: Fonts.SEMI_BOLD,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical'
                  }}>{prop.name ? prop.name : "Beacon name"}</Typography>
                <Typography
                  style={{ whiteSpace: 'pre-line' }}
                  sx={{
                    marginTop: 2,
                    fontSize: 13,
                    color: AppColor.subHeaderText,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '3',
                    WebkitBoxOrient: 'vertical'
                  }} >{prop.description ? prop.description : "Beacon details"}</Typography>
              </Box>
            </Box>
            <Box sx={{ margin: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Button
                sx={{
                  paddingX: 6,
                  borderRadius: 4,
                  whiteSpace: "nowrap",
                  color: "white",
                  background: `linear-gradient(to right bottom, ${AppColor.baseOrange}, ${alpha(AppColor.baseOrange, 0.6)})`
                }}>
                Get Directions
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const getTimeFromStringDate = (dateStr?: string) => {
    if (!dateStr || dateStr.length === 0) return "--:--";

    try {
      const date = new Date(dateStr);
      return date.getHours().toString().padStart(2, '0') + ":" + date.getMinutes().toString().padStart(2, '0');
    }
    catch
    {
      return "";
    }
  };

  const pinPreview = () => {
    const category = getCategory(prop.category ?? "");
    return (
      <Grid container>
        <Grid item xs={3} >
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                border: "4px solid " + getCategoryColor(prop.category ?? "", categoryGroupState.categories ?? []),
                backgroundColor: AppColor.componentBackground,
                ml: 8,
                mt: 6
              }}>
              {imageFaceURL ? (
                <CardMedia
                  sx={{ height: "100%", borderRadius: "50%" }}
                  component="img"
                  src={imageFaceURL}
                  alt="beacon"
                />
              ) : (
                <CardMedia
                  sx={{ height: "100%", borderRadius: "50%" }}
                  component="img"
                  src={getImageURLCategoryListIcon(category?.Id ?? "", category?.LastModified ?? "")}
                />
              )}
            </Box>
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "130px",
              ml: 2,
              mt: -1,
              borderRadius: 6,
              p: 0,
              opacity: "100%",
              backgroundColor: "white",
              border: "4px solid " + getCategoryColor(prop.category ?? "", categoryGroupState.categories ?? []),
            }}>
              <Typography component="h4"
                sx={{
                  fontWeight: Fonts.SEMI_BOLD,
                  color: "black",
                  fontSize: 14,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                  textAlign: "center",
                  p: 1,
                }}>{prop.name ? prop.name : "Beacon name"}</Typography>
            </Box>
            <Box 
              sx={{
                width: "0px", 
                height: "0px",
                ml: 15.5, 
                borderLeft: "10px solid transparent", 
                borderRight: "10px solid transparent", 
                borderTop: "10px solid", 
                borderTopColor: getCategoryColor(prop.category ?? "", categoryGroupState.categories ?? [])}}>
            </Box>
            {/* <Box sx={{ ml: 8, width: "80px", display: "flex", justifyContent: "center" }}>
              <img src="/images/pinbar.png" width={"50px"} alt="pin bar" />
            </Box> */}
          </Box>
        </Grid>
        <Grid item xs={8} ml={6}>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50%",
                width: "100px",
                height: "100px",
                border: "4px solid " + getCategoryColor(prop.category ?? "", categoryGroupState.categories ?? []),
                backgroundColor: AppColor.componentBackground,
                mt: 6,
              }}>
              <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                {imageFaceURL ? (
                  <CardMedia
                    sx={{ height: "100%", borderRadius: "50%" }}
                    component="img"
                    src={imageFaceURL}
                    alt="beacon"
                  />
                ) : (
                  <CardMedia
                    sx={{ height: "100%", borderRadius: "50%" }}
                    component="img"
                    src={getImageURLCategoryListIcon(category?.Id ?? "", category?.LastModified ?? "")}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Card
            sx={{
              minWidth: "240px",
              minHeight: "200px",
              borderRadius: 4,
              boxShadow: 8,
              padding: 3,
              mt: -13,
              backgroundColor: AppColor.componentBackground
            }}>
            <Box >
              <Box sx={{ display: "flex", justifyContent: "end" }}>
                <IconButton
                  key="close"
                  aria-label="close"
                  color="inherit"
                  size="small"
                  sx={{ color: AppColor.subHeaderText, backgroundColor: AppColor.skeletonBase, height: "30px", mt: 1 }}
                >
                  <CloseIcon sx={{ width: "20px" }} />
                </IconButton>
              </Box>
              <Typography component="h4"
                sx={{
                  mt: 4,
                  fontWeight: Fonts.SEMI_BOLD,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '1',
                  WebkitBoxOrient: 'vertical'
                }}>{prop.name ? prop.name : "Beacon name"}</Typography>
              <Typography
                style={{ whiteSpace: 'pre-line' }}
                sx={{
                  marginTop: 2,
                  fontSize: 14,
                  color: AppColor.headerText,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '3',
                  WebkitBoxOrient: 'vertical',
                  height: "70px"
                }} >{prop.description ? prop.description : "Beacon details"}</Typography>
                <CardMedia
                    sx={{ }}
                    component="img"
                    src={imageModalURL}
                  />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-start", color: AppColor.baseOrange }}>
                <Favorite sx={{ mr: 1, width: "20px" }} />
                0
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{
      height: "100%",
      width: "100%",
      backgroundColor: "white",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minWidth: "370px"
    }}>
      {viewMode === "map" ? pinPreview() : mobilePreview()}
      {/* <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}>
        <Typography sx={{ fontSize: 14 }} >Preview</Typography>
        <ToggleButtonGroup
          color="primary"
          sx={{ background: "white", margin: 2 }}
          value={viewMode}
          exclusive
          onChange={(e, newValue) => {
            if (newValue) {
              setViewMode(newValue);
            }
          }}>
          <ToggleButton value="map" size="small"><PinDropOutlinedIcon sx={{ fontSize: 16 }} /></ToggleButton>
          <ToggleButton value="mobile" size="small"><PhoneIphoneOutlinedIcon sx={{ fontSize: 16 }} /></ToggleButton>
        </ToggleButtonGroup>
      </Box> */}
    </Box >
  );
};

export default BeaconPreview;
